<template>
  <v-container fluid>
    <v-row density="compact">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <h2 class="my-0" v-if="$vuetify.display.mdAndUp">Statistiche</h2>
          <h3 class="my-0" v-else>Statistiche</h3>
        </div>
      </v-col>
      <v-col cols="12">
        <v-divider class="my-0"></v-divider>
      </v-col>
      <v-col cols="12">
        <h4 class="my-0">Pacchetti sedute in scadenza nei prossimi 15 giorni</h4>
        <p class="my-0" v-if="!loadingExpiring">{{expiringSeats.length}} risultati</p>
      </v-col>
      <v-col cols="12" class="border">
        <div style="max-height: 60vh" class="w-100 overflow-y-auto">
          <v-table density="compact" >
            <thead>
            <tr>
              <th>Utente</th>
              <th>Prodotto</th>
              <th>Venduto il</th>
              <th>Scadenza</th>
            </tr>
            </thead>

            <tbody>
            <tr v-if="loadingExpiring">
              <td colspan="4">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </td>
            </tr>
            <tr v-else-if="expiringSeats.length === 0">
              <td colspan="4" class="font-italic text-center">Non ci sono pacchetti sedute in scadenza</td>
            </tr>
            <tr v-for="s in expiringSeats" :key="s._id">
              <td>
                <a class="text-decoration-underline cursor-pointer text-primary" @click="$router.push(`/users/${s.user_id}`)">{{getUserName(s.user_id)}}</a>
              </td>
              <td>
                <a class="text-decoration-underline cursor-pointer text-primary" @click="goToProductPage(s.product_id)">{{displayProductId(s.product_id)}}</a>
              </td>
              <td>
                <a class="text-decoration-underline cursor-pointer text-primary" @click="goToPurchasePage(s.purchase_id)">{{displayPurchaseId(s.purchase_id)}}</a>
              </td>
              <td>
                {{formatDate(s.expiration)}}
              </td>
            </tr>
            </tbody>
          </v-table>
        </div>
      </v-col>
      <v-col cols="12">
        <h4 class="my-0">Pacchetti sedute attivi in un dato periodo</h4>
        <p class="my-0" v-if="!loadingActive">{{activeSeats.length}} risultati</p>
      </v-col>
      <v-col cols="6" md="4" lg="3">
        <v-text-field type="date" label="Data da" v-model="dateFrom" hide-details></v-text-field>
      </v-col>
      <v-col cols="6" md="4" lg="3">
        <v-text-field type="date" label="Data a" v-model="dateTo" hide-details></v-text-field>
      </v-col>
      <v-col cols="6" md="4" lg="3">
        <v-btn color="primary" append-icon="mdi-magnify" :disabled="!canSearch" @click="searchActive()">Cerca</v-btn>
      </v-col>

      <v-col cols="12" class="border">
        <div style="max-height: 60vh" class="w-100 overflow-y-auto">
          <v-table density="compact" >
            <thead>
            <tr>
              <th>Utente</th>
              <th>Prodotto</th>
              <th>Venduto il</th>
              <th>Scadenza</th>
            </tr>
            </thead>

            <tbody>
            <tr v-if="loadingActive">
              <td colspan="4">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </td>
            </tr>
            <tr v-else-if="activeSeats.length === 0">
              <td colspan="4" class="font-italic text-center">Non ci sono pacchetti sedute attivi nel periodo selezionato</td>
            </tr>
            <tr v-for="s in activeSeats" :key="s._id">
              <td>
                <a class="text-decoration-underline cursor-pointer text-primary" @click="$router.push(`/users/${s.user_id}`)">{{getUserName(s.user_id)}}</a>
              </td>
              <td>
                <a class="text-decoration-underline cursor-pointer text-primary" @click="goToProductPage(s.product_id)">{{displayProductId(s.product_id)}}</a>
              </td>
              <td>
                <a class="text-decoration-underline cursor-pointer text-primary" @click="goToPurchasePage(s.purchase_id)">{{displayPurchaseId(s.purchase_id)}}</a>
              </td>
              <td>
                {{formatDate(s.expiration)}}
              </td>
            </tr>
            </tbody>
          </v-table>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import {computed, defineComponent, inject, onMounted, ref} from 'vue'
import type {Globals} from "@/plugins/globals.ts";
import api from "@/api";
import type {UserSeat} from "@/models.ts";
import utils from "@/utils";
import {useRouter} from "vue-router";
import {DateTime} from "luxon";

export default defineComponent({
  name: "Stats",
  setup() {
    const globals = inject('globals') as Globals
    const router = useRouter()
    const expiringSeats = ref<UserSeat[]>([])
    const activeSeats = ref<UserSeat[]>([])
    const loadingExpiring = ref<boolean>(false)
    const loadingActive = ref<boolean>(false)

    const dateFrom = ref<string|undefined>()
    const dateTo = ref<string|undefined>()

    const loadExpiring = async ()=>{
      loadingExpiring.value = true
      const res = await api.getExpiringSeats()
      expiringSeats.value = res.user_seats ?? []
      loadingExpiring.value = false
    }

    onMounted(async ()=>{

      await Promise.all([
        globals.refreshEmpty('products'),
        globals.refreshEmpty('purchases'),
        globals.refreshEmpty('users'),
        loadExpiring()
      ])

    })

    return {
      expiringSeats,
      loadingExpiring,
      getUserName(id:string) {
        const user = globals.users.value.find(u => u._id === id)
        return user ? `${user.surname} ${user.name} (${user.email})` : `${id} (utente eliminato)`
      },
      displayProductId(id?:string):string {
        if(!id) return '-'
        const p = globals.products.value.find(p => p._id === id)
        return p ? `${p.title.it}` : `[${id}] - prodotto eliminato`
      },
      displayPurchaseId(id?:string):string {
        if(!id) return '-'
        const p = globals.purchases.value.find(p => p._id === id)
        return p ? utils.formatDateTime(p.date) : `[${id}] - vendita eliminata`
      },
      async goToProductPage(id?:string) {
        if(!id) return
        const p = globals.products.value.find(p => p._id === id)
        if(!p) return
        await router.push(`/kits/${p.kit_id}/products/${id}`)
      },
      async goToPurchasePage(id?:string) {
        if(!id) return
        const p = globals.purchases.value.find(p => p._id === id)
        if(!p) return
        await router.push(`/sells/${id}/view`)
      },
      formatDate : utils.formatDate,
      dateFrom,
      dateTo,
      canSearch : computed(()=>{
        if(!dateFrom.value || !dateTo.value) return false
        const from = DateTime.fromISO(dateFrom.value)
        if(!from || !from.isValid) return false
        const to = DateTime.fromISO(dateTo.value)
        return !(!to || !to.isValid);

      }),
      loadingActive,
      activeSeats,
      async searchActive() {
        loadingActive.value = true

        const from = DateTime.fromISO(dateFrom.value!)!
        const to = DateTime.fromISO(dateTo.value!)!

        const res = await api.getSeatsStats(from.toISODate()!, to.toISODate()!)
        activeSeats.value = res.user_seats ?? []

        loadingActive.value = false
      }
    }
  }
})
</script>


<style scoped>

</style>